import React from 'react';
import { Link } from 'react-router-dom';
import { House } from '../types/house';
import { useImage } from '../hooks/useImage';
import { Bed, Bath, Car, Ruler, DollarSign, Edit2, Trees } from 'lucide-react';

interface HouseCardProps {
  house: House;
}

const HouseCard: React.FC<HouseCardProps> = ({ house }) => {
  const displayImageUrl = useImage(house.imageId, house.imageUrl);

  const formatPrice = (price?: number) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD', maximumFractionDigits: 0 }).format(price);
  };

  const getFeelEmoji = (feel?: number) => {
    switch (feel) {
      case 1: return '😫';
      case 2: return '🙁';
      case 3: return '😐';
      case 4: return '🙂';
      case 5: return '🤩';
      default: return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col h-full">
      <div className="h-48 bg-gray-200 relative">
        {displayImageUrl ? (
          <img src={displayImageUrl} alt={house.address} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <HomeIcon className="w-12 h-12" />
          </div>
        )}
        <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-bold text-indigo-600 shadow-sm">
          Est. {formatPrice(house.estimate)}
        </div>
        {!!house.feel && (
          <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-lg shadow-sm" title="My Feel">
            {getFeelEmoji(house.feel)}
          </div>
        )}
      </div>
      
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-1 flex items-center" title={house.address}>
          {house.address}
        </h3>
        
        <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-4">
          <div className="flex items-center" title="Bedrooms">
            <Bed className="w-4 h-4 mr-1" /> {house.bedrooms}
          </div>
          <div className="flex items-center" title="Bathrooms">
            <Bath className="w-4 h-4 mr-1" /> {house.bathrooms}
          </div>
          <div className="flex items-center" title="Parking">
            <Car className="w-4 h-4 mr-1" /> {house.parking}
          </div>
          <div className="flex items-center" title="Floor Area">
            <Ruler className="w-4 h-4 mr-1" /> {house.propertySize}m²
          </div>
          {house.landSize > 0 && (
            <div className="flex items-center" title="Land Area">
              <Trees className="w-4 h-4 mr-1" /> {house.landSize}m²
            </div>
          )}
        </div>

        {!!house.myPricePrediction && (
           <div className="mb-3 p-2 bg-green-50 rounded border border-green-100">
             <div className="text-xs text-green-800 font-medium flex items-center">
               <DollarSign className="w-3 h-3 mr-1" /> My Prediction
             </div>
             <div className="text-sm font-bold text-green-700">
               {formatPrice(house.myPricePrediction)}
             </div>
           </div>
        )}

        <div className="mt-auto pt-3 border-t border-gray-100 flex justify-between items-center">
          <a 
            href={house.sourceUrl || house.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
          >
            View Listing
          </a>
          <Link 
            to={`/edit/${house.id}`}
            className="flex items-center text-sm text-gray-500 hover:text-gray-900"
          >
            <Edit2 className="w-3 h-3 mr-1" /> Edit
          </Link>
        </div>
      </div>
    </div>
  );
};

// Helper icon component for placeholder
const HomeIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

export default HouseCard;
